import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SessionKey, SessionState, ScreenMatchResult } from '../shared/models';
import { ModuleExecutorService, Module } from './module-executor.service';
import { environment } from '../../environments/environment';

/**
 * Session Pool Service
 * Manages session lifecycle for job-level execution with session reuse
 */
@Injectable({ providedIn: 'root' })
export class SessionPoolService {
  private pool = new Map<string, SessionState>();
  private authProfiles = new Map<string, any>();
  private screens = new Map<string, any>();
  private apiBaseUrl: string = environment.apiUrl;

  constructor(
    private moduleExecutor: ModuleExecutorService,
    private http: HttpClient
  ) {}

  setApiBaseUrl(url: string): void {
    this.apiBaseUrl = url;
  }

  /**
   * Initialize session pool with context from backend
   */
  async initialize(profiles: any[], screenDefs: any[]): Promise<void> {
    this.authProfiles.clear();
    this.screens.clear();
    profiles.forEach(p => this.authProfiles.set(p.id, p));
    screenDefs.forEach(s => {
      // Use id or screenId field as Map key
      const key = s.screenId || s.id;
      if (key) {
        this.screens.set(key, s);
      }
    });
  }

  /**
   * Generate unique session key from auth profile and tenant
   */
  private getSessionKey(key: SessionKey): string {
    return `${key.authProfileId}|${key.tenantId || 'default'}`;
  }

  /**
   * Acquire a session from the pool (creates if doesn't exist)
   */
  async acquireSession(key: SessionKey, policy: 'REUSE' | 'FRESH' = 'REUSE', initialUrl?: string): Promise<SessionState> {
    const sessionKey = this.getSessionKey(key);

    if (policy === 'FRESH') {
      await this.releaseSession(key);
    }

    let session = this.pool.get(sessionKey);
    if (!session) {
      const partition = `session-${sessionKey}-${Date.now()}`;
      const host: any = (window as any).host;

      if (!host?.createSessionWindow) {
        throw new Error('Session window creation not supported (host API not available)');
      }

      const result = await host.createSessionWindow({ partition, initialUrl });
      if (!result.success || !result.handle) {
        throw new Error(result.error || 'Failed to create session window');
      }

      session = {
        partition,
        windowHandle: result.handle,
        lastUsed: new Date(),
        isLoggedIn: false,
        authProfileId: key.authProfileId,
        tenantId: key.tenantId
      };
      this.pool.set(sessionKey, session);
    }

    session.lastUsed = new Date();
    return session;
  }

  /**
   * Release a session from the pool
   */
  async releaseSession(key: SessionKey): Promise<void> {
    const sessionKey = this.getSessionKey(key);
    const session = this.pool.get(sessionKey);

    if (session) {
      const host: any = (window as any).host;
      if (host?.destroySessionWindow && session.windowHandle) {
        try {
          await host.destroySessionWindow(session.windowHandle);
        } catch (error) {
          console.warn(`Failed to destroy session window ${session.windowHandle}:`, error);
        }

        await new Promise(resolve => setTimeout(resolve, 300));

        this.pool.delete(sessionKey);
      } else {
        this.pool.delete(sessionKey);
      }
    }
  }

  /**
   * Destroy all sessions in the pool
   */
  async destroyAll(): Promise<void> {
    const host: any = (window as any).host;
    
    for (const [key, session] of this.pool.entries()) {
      if (host?.destroySessionWindow && session.windowHandle) {
        try {
          await host.destroySessionWindow(session.windowHandle);
        } catch (error) {
        }
      }
    }
    this.pool.clear();
  }

  /**
   * Detect current screen using match rules
   */
  async detectCurrentScreen(sessionState: SessionState): Promise<string | null> {
    const host: any = (window as any).host;
    if (!host?.evaluateScreenRules) {
      return null;
    }

    for (const [screenId, screenDef] of this.screens.entries()) {
      try {
        // Parse matchRules if it's a string
        let matchRules = screenDef.matchRules;
        if (typeof matchRules === 'string') {
          try {
            matchRules = JSON.parse(matchRules);
          } catch (parseError) {
            continue;
          }
        }
        
        if (!Array.isArray(matchRules) || matchRules.length === 0) {
          continue;
        }
        
        const result: ScreenMatchResult = await host.evaluateScreenRules(
          sessionState.windowHandle,
          matchRules,
          screenDef.minMatch || 1
        );
        
        if (result.matched) {
          return screenId;
        } 
      } catch (error) {
      }
    }
    return null;
  }

  /**
   * Execute preconditions for a scenario
   */
  async executePreconditions(
    scenario: any,
    sessionState: SessionState,
    modules: any[]
  ): Promise<{
    success: boolean;
    authSuccess?: boolean;
    screenValidated?: boolean;
    error?: string;
    duration: number;
  }> {
    const startTime = Date.now();
    
    // Map API field names: screenId → startScreenId, screenMaster → startScreen
    const startScreenId = scenario.screenId || scenario.startScreenId;
    let { authProfileId, tenantId, skipIfSatisfied } = scenario;

    // Resolve authProfileId from authProfileInfo.authProfileIds when authProfileId is null
    if (!authProfileId && scenario.authProfileInfo && scenario.authProfileInfo !== 'null') {
      try {
        const profileInfo = typeof scenario.authProfileInfo === 'string'
          ? JSON.parse(scenario.authProfileInfo)
          : scenario.authProfileInfo;
        if (profileInfo?.authProfileIds?.length > 0) {
          // Use the first auth profile ID from the stored group
          // Fresh credentials will be fetched by the existing fetch logic below
          authProfileId = profileInfo.authProfileIds[0];
        }
      } catch (e) {
        console.warn('Failed to parse authProfileInfo for profile group resolution', e);
      }
    }

    if (!sessionState.windowHandle) {
      return {
        success: false,
        error: 'No session window handle provided',
        duration: Date.now() - startTime
      };
    }

    try {
      // Skip initial screen detection - window is on about:blank until we navigate
      // We'll detect screen after login/navigation completes
      const currentScreen = null;
      const isLoggedIn = sessionState.isLoggedIn;

      // Build execution plan
      const plan: string[] = [];

      if (authProfileId && !isLoggedIn) {
        plan.push('login');
      }

      if (tenantId && sessionState.tenantId !== tenantId) {
        plan.push('switch_tenant');
      }

      if (startScreenId && currentScreen !== startScreenId) {
        plan.push('navigate_to_screen');
      }

      if (skipIfSatisfied && plan.length === 0) {
        return {
          success: true,
          authSuccess: isLoggedIn,
          screenValidated: currentScreen === startScreenId,
          duration: Date.now() - startTime
        };
      }


      let authSuccess = isLoggedIn;
      let screenValidated = currentScreen === startScreenId;
      let loginUrl: string | undefined;

      // Execute plan
      for (const action of plan) {
        if (action === 'login') {
          const authProfile = this.authProfiles.get(authProfileId);
          
          if (!authProfile) {
            // If auth profile not in cache, use scenario.authProfile (already has decrypted credentials)
            let authProfileData = scenario.authProfile;
            
            // If authProfile not in scenario, fetch it from API
            if (!authProfileData && authProfileId) {
              try {
                // Add timeout to prevent hanging
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => {
                    reject(new Error('Auth profile fetch timeout after 10s'));
                  }, 10000)
                );
                
                const fetchPromise = firstValueFrom(
                  this.http.get(`${this.apiBaseUrl}/auth-profiles/${authProfileId}`)
                );
                
                const response: any = await Promise.race([fetchPromise, timeoutPromise]);
                
                authProfileData = response.data || response;
              } catch (fetchError: any) {
                throw new Error(`Failed to fetch auth profile ${authProfileId}: ${fetchError.message || fetchError}`);
              }
            }
            
            // Extract tenant URL for navigation
            loginUrl = authProfileData?.tenantUrl;
            if (loginUrl) {
              // Navigate session window to tenant URL
              const host: any = (window as any).host;
              if (host?.navigateSession && sessionState.windowHandle) {
                await host.navigateSession(sessionState.windowHandle, loginUrl);
                // Wait for page load
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }
            
            if (!authProfileData) {
              throw new Error(`Auth profile not found for ID: ${authProfileId}`);
            }
            
            // Check for username field - might be in different format
            const username = authProfileData.username || authProfileData.userName || authProfileData.email;
            if (!username) {
              throw new Error(`Auth profile missing username field. Available fields: ${Object.keys(authProfileData).join(', ')}`);
            }

            let loginModule: any;

            // Option 1: Use recorded login steps if available
            if (authProfileData.steps && authProfileData.steps.length > 0) {
              let steps: any[];
              try {
                steps = typeof authProfileData.steps === 'string' 
                  ? JSON.parse(authProfileData.steps) 
                  : authProfileData.steps;
              } catch (parseError) {
                throw new Error(`Failed to parse login steps: ${parseError}`);
              }
              
              if (!Array.isArray(steps) || steps.length === 0) {
                throw new Error('Login steps must be a non-empty array');
              }
              
              // CRITICAL: Filter to only include actual login steps (username, password, submit)
              // Stop IMMEDIATELY after submit button - everything else is post-login navigation
              let loginSteps: any[] = [];
              let foundUsername = false;
              let foundPassword = false;
              let foundSubmit = false;
              
              for (let i = 0; i < steps.length; i++) {
                const step = steps[i];
                const selectorStr = JSON.stringify(step.selector || {}).toLowerCase();
                const description = (step.description || '').toLowerCase();
                const text = (step.text || '').toLowerCase();
                const action = (step.action || '').toLowerCase();
                
                // STRICT: Only username, password, and submit button
                const isUsername = (action === 'fill' || action === 'type') && 
                                   (selectorStr.includes('username') || 
                                    description.includes('username') || 
                                    text.includes('username'));
                                    
                const isPassword = (action === 'fill' || action === 'type') && 
                                   (selectorStr.includes('password') || 
                                    description.includes('password') || 
                                    text.includes('password'));
                                    
                const isSubmit = (action === 'click') && 
                                 (selectorStr.includes('submit') || 
                                  selectorStr.includes('sign in') ||
                                  selectorStr.includes('login') ||
                                  selectorStr.includes('signin') ||
                                  text.includes('sign in') ||
                                  text.includes('sign-in') ||
                                  text.includes('signin') ||
                                  text.includes('login') ||
                                  description.includes('sign in') ||
                                  description.includes('sign-in') ||
                                  description.includes('signin') ||
                                  description.includes('login')) &&
                                 // Exclude home/navigation clicks
                                 !text.includes('home') &&
                                 !description.includes('home') &&
                                 !selectorStr.includes('home');
                
                if (isUsername || isPassword || isSubmit) {
                  loginSteps.push(step);
                  if (isUsername) foundUsername = true;
                  if (isPassword) foundPassword = true;
                  if (isSubmit) foundSubmit = true;
                  
                  // STOP IMMEDIATELY after submit button (login complete)
                  if (isSubmit) {
                    break;
                  }
                } else {
                  // If we already have username + password, stop here even if no submit found
                  // (some logins auto-submit or use Enter key)
                  if (foundUsername && foundPassword && i > 2) {
                    break;
                  }
                }
              }
              
              // CRITICAL: If no submit found, try to find ANY click after password
              if (!foundSubmit && loginSteps.length >= 2) {
                let passwordIndex = -1;
                for (let i = 0; i < steps.length; i++) {
                  const step = steps[i];
                  const selectorStr = JSON.stringify(step.selector || {}).toLowerCase();
                  const description = (step.description || '').toLowerCase();
                  if (selectorStr.includes('password') || description.includes('password')) {
                    passwordIndex = i;
                  }
                }
                
                // Look for first click after password
                if (passwordIndex >= 0 && passwordIndex < steps.length - 1) {
                  for (let i = passwordIndex + 1; i < steps.length; i++) {
                    const step = steps[i];
                    if (step.action === 'click') {
                      loginSteps.push(step);
                      foundSubmit = true;
                      break;
                    }
                  }
                }
              }
              
              if (loginSteps.length === 0) {
                loginModule = this.moduleExecutor.generateLoginModule({
                  username: authProfileData.username,
                  password: authProfileData.password || authProfileData.secretKey,
                  profileName: authProfileData.name || authProfileData.profileName
                });
              } else {
                loginModule = {
                  moduleType: 'login',
                  name: `Login as ${authProfileData.profileName || authProfileData.username}`,
                  description: 'Filtered login steps with placeholder injection',
                  steps: loginSteps.map((step: any, index: number) => {
                    // Detect if this is a username or password field based on selector/description
                    if (step.action === 'fill') {
                      const selectorStr = JSON.stringify(step.selector || {}).toLowerCase();
                      const description = (step.description || '').toLowerCase();
                      const text = (step.text || '').toLowerCase();
                      const name = (step.selector?.name || '').toLowerCase();
                      
                      // Check if this is a username field
                      if (selectorStr.includes('username') || 
                          description.includes('username') || 
                          text.includes('username') ||
                          name.includes('username') ||
                          step.selector?.css?.includes('username') ||
                          step.selector?.name === 'Username') {
                        // CRITICAL FIX: Preserve original value before placeholder replacement
                        // Store in additionalInfo so command-based steps can recover actual value
                        if (!step.originalValue && step.value && step.value !== '{{username}}') {
                          step.originalValue = step.value;
                        }
                        step.value = '{{username}}';
                      }
                      // Check if this is a password field
                      else if (selectorStr.includes('password') || 
                               description.includes('password') || 
                               text.includes('password') ||
                               name.includes('password') ||
                               step.selector?.css?.includes('password') ||
                               step.selector?.name === 'Password') {
                        // CRITICAL FIX: Preserve original value before placeholder replacement
                        // Store in additionalInfo so command-based steps can recover actual value
                        if (!step.originalValue && step.value && step.value !== '{{password}}') {
                          step.originalValue = step.value;
                        }
                        step.value = '{{password}}';
                      }
                    }
                    return step;
                  })
                };
              }
            } else {
              // Option 2: Generate login module with default/custom selectors
              loginModule = this.moduleExecutor.generateLoginModule({
                username: authProfileData.username,
                password: authProfileData.password || authProfileData.secretKey,
                profileName: authProfileData.name || authProfileData.profileName
              });
            }

            const result = await this.moduleExecutor.executeModule(
              loginModule,
              sessionState.windowHandle!,
              {
                username: authProfileData.username,
                password: authProfileData.password || authProfileData.secretKey
              }
            );

            if (!result.success) {
              throw new Error(`Login failed: ${result.error}`);
            }

            sessionState.isLoggedIn = true;
            authSuccess = true;
            
            // CRITICAL: After login, wait for page to load and stabilize
            await new Promise(resolve => setTimeout(resolve, 5000));
          } else {
            // Use auth profile from pool (runner mode) - treat same as !authProfile path
            
            // Fetch full profile from API if cached version is missing credentials/steps
            let authProfileData = authProfile;
            if (!authProfileData.username || !authProfileData.secretKey || !authProfileData.steps) {
              try {
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Auth profile fetch timeout after 10s')), 10000)
                );
                
                const fetchPromise = firstValueFrom(
                  this.http.get(`${this.apiBaseUrl}/auth-profiles/${authProfileId}`)
                );
                
                const response: any = await Promise.race([fetchPromise, timeoutPromise]);
                authProfileData = response.data || response;
              } catch (fetchError: any) {
                throw new Error(`Failed to fetch auth profile ${authProfileId}: ${fetchError.message || fetchError}`);
              }
            }
            
            // Navigate to tenant URL
            loginUrl = authProfileData.tenantUrl;
            if (loginUrl) {
              const host: any = (window as any).host;
              if (host?.navigateSession && sessionState.windowHandle) {
                await host.navigateSession(sessionState.windowHandle, loginUrl);
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }
            
            // Use the SAME login module generation logic as the !authProfile path
            let loginModule: any;
            
            if (authProfileData.steps && authProfileData.steps.length > 0) {
              // Parse and filter login steps (lines 320-475 logic)
              let steps: any[];
              try {
                steps = typeof authProfileData.steps === 'string' 
                  ? JSON.parse(authProfileData.steps) 
                  : authProfileData.steps;
              } catch (parseError) {
                throw new Error(`Failed to parse login steps: ${parseError}`);
              }
              
              // Filter to login steps only
              let loginSteps: any[] = [];
              let foundUsername = false;
              let foundPassword = false;
              let foundSubmit = false;
              
              for (let i = 0; i < steps.length; i++) {
                const step = steps[i];
                const selectorStr = JSON.stringify(step.selector || {}).toLowerCase();
                const description = (step.description || '').toLowerCase();
                const text = (step.text || '').toLowerCase();
                const action = (step.action || '').toLowerCase();
                
                const isUsername = (action === 'fill' || action === 'type') && 
                                   (selectorStr.includes('username') || description.includes('username') || text.includes('username'));
                const isPassword = (action === 'fill' || action === 'type') && 
                                   (selectorStr.includes('password') || description.includes('password') || text.includes('password'));
                const isSubmit = (action === 'click') && 
                                 (selectorStr.includes('submit') || selectorStr.includes('sign in') || selectorStr.includes('login') ||
                                  text.includes('sign in') || text.includes('login') || description.includes('sign in') || description.includes('login'));
                
                if (isUsername || isPassword || isSubmit) {
                  loginSteps.push(step);
                  if (isUsername) foundUsername = true;
                  if (isPassword) foundPassword = true;
                  if (isSubmit) foundSubmit = true;
                  
                  if (isSubmit) break; // Stop after submit
                } else if (foundUsername && foundPassword && i > 2) {
                  break;
                }
              }
              
              loginModule = {
                moduleType: 'login',
                name: `Login as ${authProfileData.profileName || authProfileData.username}`,
                description: 'Filtered login steps with placeholder injection',
                steps: loginSteps.map((step: any) => {
                  if (step.action === 'fill') {
                    const selectorStr = JSON.stringify(step.selector || {}).toLowerCase();
                    const description = (step.description || '').toLowerCase();
                    
                    if (selectorStr.includes('username') || description.includes('username')) {
                      if (!step.originalValue && step.value && step.value !== '{{username}}') {
                        step.originalValue = step.value;
                      }
                      step.value = '{{username}}';
                    } else if (selectorStr.includes('password') || description.includes('password')) {
                      if (!step.originalValue && step.value && step.value !== '{{password}}') {
                        step.originalValue = step.value;
                      }
                      step.value = '{{password}}';
                    }
                  }
                  return step;
                })
              };
            } else {
              // Fallback: generate default login module
              loginModule = this.moduleExecutor.generateLoginModule({
                username: authProfileData.username,
                password: authProfileData.secretKey,
                profileName: authProfileData.profileName
              });
            }
            
            // Execute login module
            const result = await this.moduleExecutor.executeModule(
              loginModule,
              sessionState.windowHandle!,
              {
                username: authProfileData.username,
                password: authProfileData.secretKey
              }
            );
            
            if (!result.success) {
              throw new Error(`Login failed: ${result.error}`);
            }
            
            sessionState.isLoggedIn = true;
            authSuccess = true;
            
            await new Promise(resolve => setTimeout(resolve, 5000));
          }
        } else if (action === 'switch_tenant') {
          const tenantModule = modules.find(m => m.moduleType === 'switch_tenant');
          if (tenantModule) {
            const result = await this.moduleExecutor.executeModule(
              tenantModule,
              sessionState.windowHandle!,
              { tenantId }
            );

            if (!result.success) {
              throw new Error(`Tenant switch failed: ${result.error}`);
            }

            sessionState.tenantId = tenantId;
          }
        } else if (action === 'navigate_to_screen') {
          // STEP 1: Verify if we're already on the target screen
          const currentScreen = await this.detectCurrentScreen(sessionState);
          
          if (currentScreen === startScreenId) {
            screenValidated = true;
            continue; // Skip navigation, already there
          }
          
          // STEP 2: Get screen definition
          const screen = this.screens.get(startScreenId) || scenario.screenMaster || scenario.startScreen;
          
          if (!screen) {
            throw new Error(`Screen definition not found: ${startScreenId}`);
          }

          // STEP 3: Try navigate module first (if available)
          if (screen.navigateModuleId) {
            const navModule = modules.find(m => m.id === screen.navigateModuleId);
            if (navModule) {
              const result = await this.moduleExecutor.executeModule(
                navModule,
                sessionState.windowHandle!,
                {}
              );

              if (result.success) {
                // Verify screen after navigation
                const verifiedScreen = await this.detectCurrentScreen(sessionState);
                if (verifiedScreen === startScreenId) {
                  screenValidated = true;
                  continue; // Success, move to next action
                }
              }
            }
          }
          
          // STEP 4: Fallback to proceedSteps/fallbackSteps (recorded navigation steps)
          if (!screen.navigateModuleId || modules.length === 0) {
            let fallbackSteps: any[] = [];

            // Parse fallbackSteps from different possible sources
            // API returns proceedSteps as JSON string in screenMaster
            if (screen.proceedSteps) {
              try {
                fallbackSteps = typeof screen.proceedSteps === 'string' 
                  ? JSON.parse(screen.proceedSteps) 
                  : screen.proceedSteps;
              } catch (error) {
              }
            } else if (screen.additionalInfo) {
              try {
                const parsed = typeof screen.additionalInfo === 'string' 
                  ? JSON.parse(screen.additionalInfo) 
                  : screen.additionalInfo;
                
                fallbackSteps = parsed.fallbackSteps || [];
              } catch (error) {
              }
            } else if (screen.fallbackSteps && Array.isArray(screen.fallbackSteps)) {
              fallbackSteps = screen.fallbackSteps;
            }

            if (fallbackSteps.length > 0) {
              
              const navModule = this.moduleExecutor.generateNavigationModule(
                { screenId: startScreenId, displayName: screen.displayName || screen.screenName },
                fallbackSteps
              );

              const result = await this.moduleExecutor.executeModule(
                navModule,
                sessionState.windowHandle!,
                {}
              );

              if (!result.success) {
                throw new Error(`Fallback navigation failed: ${result.error}`);
              }
              
              // Wait for page to settle after navigation
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              // Verify screen after fallback navigation
              const verifiedScreen = await this.detectCurrentScreen(sessionState);
              if (verifiedScreen === startScreenId) {
                screenValidated = true;
              } else {
                screenValidated = false;
              }
            } else {
              screenValidated = false;
            }
          }
        }
      }

      // Verify final screen state
      const finalScreen = await this.detectCurrentScreen(sessionState);
      
      if (startScreenId && finalScreen !== startScreenId) {
        screenValidated = false;
      } else {
        screenValidated = true;
      }

      return {
        success: true,
        authSuccess,
        screenValidated,
        duration: Date.now() - startTime
      };

    } catch (error: any) {
      return {
        success: false,
        error: error?.message || String(error),
        duration: Date.now() - startTime
      };
    }
  }

  /**
   * Get pool statistics
   */
  getStats() {
    return {
      activeSessions: this.pool.size,
      profiles: this.authProfiles.size,
      screens: this.screens.size
    };
  }
}
